/** @file Option renders a single selectable option in the multi/single choice components. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Description } from '@/components/Form/components/Description';
import * as constants from '@/components/Form/constants';
import * as utils from '@/utils';
import { commonProps } from './shared';
/** The individual option UI element within the choice components. */
export const Option = tsx
    .componentFactoryOf()
    .create({
    name: constants.nameOption,
    props: {
        optionData: p.ofType().required,
        /** See {@link commonProps}. */
        name: commonProps.name,
        /** See {@link commonProps}. */
        variant: commonProps.variant,
        /** @inheritdoc */
        fieldsetDisabled: commonProps.disabled,
        /** If the option is readonly. */
        fieldsetReadonly: commonProps.readonly,
        /** The typography preset of the label. */
        labelClass: commonProps.optionLabelClass,
        /** If the option is selected. */
        checked: p(Boolean).default(false),
    },
    /** @returns Internal state of the Option component. */
    data() {
        return {
            descriptionId: `${constants.nameOption}-${this.name ?? ''}-desc-${utils.getUniqueId()}`,
        };
    },
    computed: {
        /** @returns Classes for the label element. */
        labelElementClass() {
            const isDisabled = this.fieldsetDisabled || this.optionData.disabled === true;
            const variantClasses = {
                default: [
                    'p-1 rounded-sm',
                    this.ifNonInteractiveStyle({
                        interactive: 'hover:bg-actionable-5 dark:hover:bg-actionable-60',
                    }),
                ],
                'card-like': [
                    'px-3! py-2 min-h-6',
                    'focus-within:outline-2 -outline-offset-2',
                    'outline-actionable-40',
                    this.ifNonInteractiveStyle({
                        interactive: this.ifCheckedStyle({
                            checked: [
                                'bg-actionable-5 dark:bg-actionable-60',
                                'hover:bg-actionable-10 dark:hover:bg-actionable-50',
                            ],
                            unchecked: 'hover:bg-actionable-2 dark:hover:bg-actionable-65',
                        }),
                        notInteractive: 'bg-neutral-1 dark:bg-neutral-60',
                    }),
                ],
                quiz: [
                    'px-3 py-2 rounded-sm',
                    this.ifNonInteractiveStyle({
                        interactive: [
                            'border-2',
                            this.ifCheckedStyle({
                                checked: 'border-actionable-10 dark:border-actionable-60',
                                unchecked: 'border-transparent hover:border-actionable-5 dark:hover:border-actionable-65',
                            }),
                        ],
                    }),
                    '[&_input]:self-start [&_input]:mt-1.25',
                ],
            }[this.variant];
            return [
                constants.nameOption,
                'grid gap-x-2 gap-y-0.5 items-center',
                'transition-colors duration-100',
                'm-0',
                this.ifNonInteractiveStyle({
                    interactive: [
                        'cursor-pointer',
                        this.ifCheckedStyle({
                            checked: 'text-actionable-70 dark:text-actionable-5',
                            unchecked: 'text-neutral-80 dark:text-white',
                        }),
                    ],
                    notInteractive: [
                        {
                            'cursor-not-allowed': isDisabled,
                        },
                        'text-neutral-50 dark:text-neutral-20',
                    ],
                }),
                variantClasses,
            ];
        },
    },
    methods: {
        /**
         * Helper method to return different classes based on current checked state.
         *
         * Useful when you don't want the classes to be the key of the object and the value to be the condition.
         *
         * @param o The object with classes.
         * @param o.checked Classes to return when this.checked is true.
         * @param o.unchecked Classes to return when this.checked is false.
         * @returns Some classes for the Vue component.
         */
        ifCheckedStyle(o) {
            if (this.checked) {
                return o.checked;
            }
            return o.unchecked;
        },
        /**
         * Helper method to return different classes based on current checked state.
         *
         * Useful when you don't want the classes to be the key of the object and the value to be the condition.
         *
         * @param o The object with classes.
         * @param o.disabled Classes to return when this.disabled is true.
         * @param o.enabled Classes to return when this.disabled is false.
         * @returns Some classes for the Vue component.
         */
        ifNonInteractiveStyle(o) {
            if (this.fieldsetDisabled ||
                this.fieldsetReadonly ||
                this.optionData.disabled === true) {
                return o.notInteractive;
            }
            return o.interactive;
        },
    },
    /** @returns Node of the component. */
    render() {
        const hasDescription = this.optionData.description != null;
        return (<label class={this.labelElementClass} data-cy={constants.nameOption} data-checked={this.checked} data-disabled={this.optionData.disabled === true}>
                    {utils.renderScopedSlot({ descriptionId: this.descriptionId }, this.$scopedSlots.inputs, () => utils.emptyVNode())}
                    <span data-cy="option-label" class={[this.labelClass]}>
                        {this.optionData.label}
                    </span>
                    {utils.ifOrEmpty(hasDescription, () => (<Description class={{
                    'text-neutral-50 dark:text-neutral-20': this.optionData.disabled === true,
                    'col-start-2': this.variant === 'quiz',
                }} id={this.descriptionId}>
                            {this.optionData.description}
                        </Description>))}
                </label>);
    },
});
